import { CenteredLoading, CustomOidcSecure, useMultiLngHandler } from '@healthhub/react-ui';
import type { PropsWithChildren } from 'react';
import React, { Suspense } from 'react';

import { i18n } from 'i18n';

import { OutlinedRightIcon } from '@shared/assets-icon';
import { SmartBanner } from '@shared/components/V2';
import { useExtendedRouter } from '@shared/hooks/useExtendedRouter';
import { setIdTokenCookies } from '@shared/utils/cookie/setIdTokenCookies';
import { isDev } from '@shared/utils/environment';

import PageContent from '../PageContent';
import PageFooter from '../PageFooter';
import PageHeader from '../PageHeader';

interface PortalLayoutProps {
  antiSinGovNotice?: React.ReactNode;
  contentClassName?: string;
  importantNotices?: React.ReactNode;
  isPublic?: boolean;
  noStyleContent?: boolean;
}

const PortalLayout = ({
  antiSinGovNotice,
  children,
  contentClassName,
  importantNotices,
  isPublic,
  noStyleContent,
}: PropsWithChildren<PortalLayoutProps>) => {
  const { availableLanguages } = useExtendedRouter();

  useMultiLngHandler(i18n, availableLanguages);

  return (
    <CustomOidcSecure isDisabled={isPublic ?? isDev()} setIdTokenCookies={setIdTokenCookies}>
      <a
        className="absolute top-6 left-6 font-medium rounded-2xl border-2 flex items-center gap-0.5 text-lg p-4 -translate-y-[200%] border-functional-black bg-white shadow-[0px_8px_30px_0px_rgba(0,0,0,0.3)] text-functional-black focus:z-50 focus:translate-y-0"
        href="#main-content"
        onClick={(e) => {
          e.preventDefault();
          const mainContent = document.getElementById('main-content');
          if (mainContent) {
            mainContent.focus();
          }
        }}
      >
        Skip to main content
        <OutlinedRightIcon className="size-4 inline-block [&_path]:stroke-functional-black" />
      </a>
      <div className="flex h-screen flex-col">
        <SmartBanner />
        <div id="Notices">
          {antiSinGovNotice}
          {importantNotices}
        </div>
        <PageHeader />
        <PageContent className={contentClassName} noStyleContent={noStyleContent}>
          <Suspense
            fallback={
              <div className="h-full">
                <CenteredLoading fullScreen={false} />
              </div>
            }
          >
            {children}
          </Suspense>
        </PageContent>
        <PageFooter />
      </div>
    </CustomOidcSecure>
  );
};

export default PortalLayout;
